/* eslint-disable @typescript-eslint/no-unused-vars */
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import ErrorIcon from "@mui/icons-material/Error";
import HistoryIcon from "@mui/icons-material/History";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Slide,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField, Typography
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { TransferOrderPartDetails } from "../../../@types/DRO/GetPartsListResponse";
import {
  SalesOrderDetail,
  SalesOrderLineDTO,
} from "../../../@types/DRO/SalesOrderResponse";
import { UpdateSalesOrderRequestDto } from "../../../@types/DTO/UpdateSalesOrderBody";
import { AllPartApi } from "../../../api/GetParts/GetAllPartsApi";
import { SalesOrderApi } from "../../../api/OrderManagement/SalesOrderApi";
import { SalesOrderHistory } from "../../../components/UI/OrderHistory/SalesOrderHistory"; // adjust path
import { useLoading } from "../../../views/Loader/LoadingContext";
import {
  AppAutoCompleteWithSearchAPI,
  OptionType,
} from "../../Layouts/AppAutoCompleteWithSearchAPI/AppAutoCompleteWithSearchAPI";

interface BinMapping {
  binId: number;
  binName: string;
  quantity: number;
}

interface SalesOrderItem extends TransferOrderPartDetails {
  productId: number;
  productName: string;
  quantity: number;
  price: number;
  tax: number;
  availableQuantity?: number;
  isOutOfStock?: boolean;
  isNewItem?: boolean;
  partProductType?: string;
  binIds?: number[];
  binName?: string[];
  binQuantity?: number[];
}

interface VendorAllocation {
  vendorId?: number;
  vendorName?: string;
  quantity?: number;
}

// Transition component for dialog animations
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ViewSalesOrder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") || "";
  const { setLoading } = useLoading();
  const [orderDetails, setOrderDetails] = useState<SalesOrderDetail | null>(
    null
  );
  const [loadingError, setLoadingError] = useState(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editedQuantities, setEditedQuantities] = useState<{
    [key: string]: string;
  }>({});
  const [isClosing, setIsClosing] = useState<boolean>(false);

  // State for new items
  const [newItems, setNewItems] = useState<SalesOrderItem[]>([]);
  const [selectedPartIds, setSelectedPartIds] = useState<Set<number>>(
    new Set()
  );
  const [partsRefreshKey, setPartsRefreshKey] = useState(0);
  const [deletedExistingItems, setDeletedExistingItems] = useState<Set<number>>(
    new Set()
  );

  // Enhanced notification states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "info" | "warning"
  >("success");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogSeverity, setDialogSeverity] = useState<"success" | "error">(
    "success"
  );
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  // const [selectedBinsByPartKey, setSelectedBinsByPartKey] = useState<
  //   Record<string, BinMapping[]>
  // >({});
  // const [binDialogOpen, setBinDialogOpen] = useState(false);
  // const [binDialogTitle, setBinDialogTitle] = useState("");
  // const [binDialogPartKey, setBinDialogPartKey] = useState<string | null>(null);
  // const [binDialogMappings, setBinDialogMappings] = useState<BinMapping[]>([]);
  // const [binDialogSelectedIndices, setBinDialogSelectedIndices] = useState<
  //   Set<number>
  // >(new Set());
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<"close" | null>(null);

  // Helper function to show snackbar notifications
  const showSnackbar = (
    message: string,
    severity: "success" | "error" | "info" | "warning"
  ) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Helper function to show dialog notifications
  const showDialog = (
    title: string,
    message: string,
    severity: "success" | "error"
  ) => {
    setDialogTitle(title);
    setDialogMessage(message);
    setDialogSeverity(severity);
    setDialogOpen(true);
  };

  // Extract error message from API response
  const extractErrorMessage = (error: any): string => {
    if (error.response?.data?.message) {
      return error.response.data.message;
    } else if (error.response?.data?.error) {
      return error.response.data.error;
    } else if (error.message) {
      return error.message;
    }
    return "An unexpected error occurred. Please try again.";
  };

  const normalizeListValues = (
    values?: Array<string | number | null | undefined>
  ): string[] => {
    return (values ?? [])
      .map((value) => `${value}`.trim())
      .filter((value) => value.length > 0);
  };

  const getListPreview = (values: string[]): string => {
    if (values.length === 0) {
      return "-";
    }

    const preview = values.slice(0, 2).join(", ");
    return values.length > 2 ? `${preview}, +${values.length - 2} more` : preview;
  };

  const getVendorAllocations = (
    item: SalesOrderLineDTO | SalesOrderItem
  ): VendorAllocation[] => {
    const allocations = (item as SalesOrderLineDTO & {
      vendorAllocations?: VendorAllocation[];
    }).vendorAllocations;

    return Array.isArray(allocations) ? allocations : [];
  };

  const renderVendorAllocations = (item: SalesOrderLineDTO | SalesOrderItem) => {
    const allocations = getVendorAllocations(item);

    if (allocations.length === 0) {
      return "-";
    }

    return (
      <Box
        sx={{
          minWidth: 190,
          overflow: "hidden",
        }}>
         
        {allocations.map((allocation, allocationIndex) => (
          <Box
            key={allocation.vendorId ?? `${allocation.vendorName}-${allocationIndex}`}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              py: 0.5,
              fontSize: "0.8rem",
            }}>
            <Typography variant="body2" sx={{ fontSize: "inherit" }}>
              {allocation.vendorName || "-"}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: "inherit", fontWeight: 600, whiteSpace: "nowrap" }}>
              Qty: {allocation.quantity ?? 0}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  // const mapBinArrays = (
  //   binIds?: number[],
  //   binNames?: string[],
  //   binQuantities?: number[]
  // ): BinMapping[] => {
  //   return (binNames ?? [])
  //     .map((name, index) => ({
  //       binId: binIds?.[index] ?? 0,
  //       binName: `${name}`.trim(),
  //       quantity: binQuantities?.[index] ?? 0,
  //     }))
  //     .filter(
  //       (mapping) => mapping.binName.length > 0 && mapping.binId > 0
  //     );
  // };

  // const getSelectedBinsForPart = (partKey: string): BinMapping[] => {
  //   return selectedBinsByPartKey[partKey] ?? [];
  // };

  // const handleOpenBinDialog = (
  //   partKey: string,
  //   title: string,
  //   binIds?: number[],
  //   binNames?: string[],
  //   binQuantities?: number[]
  // ) => {
  //   const mappings = mapBinArrays(binIds, binNames, binQuantities);
  //   if (mappings.length === 0) {
  //     return;
  //   }

  //   const existingSelection = selectedBinsByPartKey[partKey];
  //   let selectedIndices: Set<number>;

  //   if (existingSelection?.length) {
  //     selectedIndices = new Set(
  //       existingSelection
  //         .map((selected) =>
  //           mappings.findIndex((mapping) => mapping.binId === selected.binId)
  //         )
  //         .filter((index) => index >= 0)
  //     );
  //   } else {
  //     selectedIndices = new Set();
  //   }

  //   setBinDialogPartKey(partKey);
  //   setBinDialogTitle(title);
  //   setBinDialogMappings(mappings);
  //   setBinDialogSelectedIndices(selectedIndices);
  //   setBinDialogOpen(true);
  // };

  // const handleToggleBinSelection = (index: number) => {
  //   setBinDialogSelectedIndices((prev) => {
  //     const next = new Set(prev);
  //     if (next.has(index)) {
  //       next.delete(index);
  //     } else {
  //       next.add(index);
  //     }
  //     return next;
  //   });
  // };

  // const handleCloseBinDialog = () => {
  //   if (binDialogPartKey) {
  //     const selected = Array.from(binDialogSelectedIndices)
  //       .sort((a, b) => a - b)
  //       .map((index) => binDialogMappings[index])
  //       .filter(Boolean);

  //     setSelectedBinsByPartKey((prev) => ({
  //       ...prev,
  //       [binDialogPartKey]: selected,
  //     }));
  //   }

  //   setBinDialogOpen(false);
  //   setBinDialogTitle("");
  //   setBinDialogPartKey(null);
  //   setBinDialogMappings([]);
  //   setBinDialogSelectedIndices(new Set());
  // };

  // const renderBinMappingCell = (
  //   partKey: string,
  //   title: string,
  //   binIds?: number[],
  //   binNames?: string[],
  //   binQuantities?: number[]
  // ) => {
  //   const mappings = mapBinArrays(binIds, binNames, binQuantities);

  //   if (mappings.length === 0) {
  //     return <TableCell>-</TableCell>;
  //   }

  //   const selectedBins = getSelectedBinsForPart(partKey);
  //   const previewItems = selectedBins.map((mapping) => mapping.binName);
  //   const displayText =
  //     previewItems.length > 0 ? getListPreview(previewItems) : "[ Select Bin ]";
  //   const hasSelection = previewItems.length > 0;

  //   return (
  //     <TableCell>
  //       <Tooltip
  //         arrow
  //         placement="top"
  //         title={
  //           <Box sx={{ p: 0.5, maxWidth: 280 }}>
  //             {mappings.map((mapping, index) => (
  //               <Typography
  //                 key={`${partKey}-${mapping.binId}-${index}`}
  //                 variant="body2"
  //                 sx={{ lineHeight: 1.4 }}>
  //                 {mapping.binName}: {mapping.quantity}
  //               </Typography>
  //             ))}
  //             <Typography
  //               variant="caption"
  //               sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
  //               Click to view and select bins
  //             </Typography>
  //           </Box>
  //         }>
  //         <Box
  //           role="button"
  //           tabIndex={0}
  //           onClick={() =>
  //             handleOpenBinDialog(
  //               partKey,
  //               title,
  //               binIds,
  //               binNames,
  //               binQuantities
  //             )
  //           }
  //           onKeyDown={(event) => {
  //             if (event.key === "Enter" || event.key === " ") {
  //               event.preventDefault();
  //               handleOpenBinDialog(
  //                 partKey,
  //                 title,
  //                 binIds,
  //                 binNames,
  //                 binQuantities
  //               );
  //             }
  //           }}
  //           sx={{
  //             cursor: "pointer",
  //             display: "inline-block",
  //             maxWidth: "100%",
  //             color: hasSelection ? "primary.main" : "text.secondary",
  //           }}>
  //           <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
  //             {displayText}
  //           </Typography>
  //         </Box>
  //       </Tooltip>
  //     </TableCell>
  //   );
  // };

  useEffect(() => {
    fetchOrderDetails();
  }, [id]);

  const fetchOrderDetails = async () => {
    if (!id) {
      setLoadingError(true);
      showSnackbar("Invalid order ID", "error");
      return;
    }

    setLoading(true);
    try {
      const response = await SalesOrderApi.api.getSingleSalesOrderDetails(
        parseInt(id)
      );
      setOrderDetails(response.data);
      // Initialize edited quantities as strings
      const quantities: { [key: string]: string } = {};
      response.data.orderLineDtos?.forEach((line: SalesOrderLineDTO) => {
        quantities[`existing-${line.partId}`] = line.quantity.toString();
      });
      setEditedQuantities(quantities);

      // Initialize selected part IDs
      const partIds = new Set<number>();
      response.data.orderLineDtos?.forEach((line: SalesOrderLineDTO) => {
        partIds.add(line.partId);
      });
      setSelectedPartIds(partIds);
    } catch (error) {
      console.error("Error fetching sales order details:", error);
      setLoadingError(true);
      const errorMessage = extractErrorMessage(error);
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  // Fetch parts with warehouse context
  const fetchPaginatedPartsBySearchTerm = useCallback(
    (data: {
      currentPageNumber: number;
      currentSearchQueryTerm: string;
    }): Promise<{
      options: OptionType<SalesOrderItem>[];
      hasMore: boolean;
    }> => {
      if (!orderDetails?.fromWarehouseDTO?.deliveryWarehouseId) {
        return Promise.resolve({ options: [], hasMore: false });
      }

      return AllPartApi.api
        .getPartList(
          data.currentPageNumber,
          10,
          data.currentSearchQueryTerm,
          undefined,
          undefined,
          undefined,
          orderDetails.fromWarehouseDTO.deliveryWarehouseId,
          undefined,
          true
        )
        .then(({ data: response }) => {
          // Filter out already selected parts
          const availableParts = response.data.filter(
            (part: any) =>
              !selectedPartIds.has(part.id) &&
              !deletedExistingItems.has(part.id)
          );

          return {
            options: availableParts.map((part: any) => ({
              value: part.id.toString(),
              label: part.isOutOfStock
                ? `${part.description} (Out of Stock)`
                : part.description,
              data: {
                ...part,
                productId: part.id,
                productName: part.description,
                quantity: 0,
                price: part.salesPrice,
                tax: part.tax || 0,
                availableQuantity: part.quantity || 0,
                isOutOfStock: part.isOutOfStock || false,
              } as SalesOrderItem,
            })),
            hasMore: data.currentPageNumber < response.totalPages - 1,
          };
        })
        .catch(() => ({ options: [], hasMore: false }));
    },
    [orderDetails, selectedPartIds, deletedExistingItems]
  );

  const calculateTotalCost = (orderLines: SalesOrderLineDTO[]) => {
    let total = 0;

    // Calculate total for existing items (excluding deleted ones)
    orderLines.forEach((line) => {
      if (!deletedExistingItems.has(line.partId)) {
        const quantityStr = editedQuantities[`existing-${line.partId}`];
        const quantity = quantityStr
          ? parseFloat(quantityStr) || 0
          : line.quantity;
        total += quantity * line.price;
      }
    });

    // Calculate total for new items
    newItems.forEach((item) => {
      if (item.productId > 0 && !item.isOutOfStock) {
        const quantityStr = editedQuantities[`new-${item.productId}`];
        const quantity = quantityStr
          ? parseFloat(quantityStr) || 0
          : item.quantity;
        total += quantity * item.price;
      }
    });

    return total;
  };

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    // Reset quantities to original values
    if (orderDetails?.orderLineDtos) {
      const quantities: { [key: string]: string } = {};
      orderDetails.orderLineDtos.forEach((line) => {
        quantities[`existing-${line.partId}`] = line.quantity.toString();
      });
      setEditedQuantities(quantities);
    }
    setNewItems([]);
    const partIds = new Set<number>();
    orderDetails?.orderLineDtos?.forEach((line: SalesOrderLineDTO) => {
      partIds.add(line.partId);
    });
    setSelectedPartIds(partIds);
    setDeletedExistingItems(new Set());
  };

  const handleQuantityChange = (key: string, newValue: string) => {
    // Allow empty string for clearing the field
    if (newValue === "") {
      setEditedQuantities((prev) => ({
        ...prev,
        [key]: newValue,
      }));
      return;
    }

    // Check if this is an existing item
    if (key.startsWith("existing-")) {
      const partId = parseInt(key.replace("existing-", ""));
      const existingItem = orderDetails?.orderLineDtos?.find(
        (item) => item.partId === partId
      );
      if (existingItem) {
        // If partProductType is 'SERVICE', allow 0 and skip availableQuantity check
        if (existingItem.partProductType === "SERVICE") {
          if (!/^\d*\.?\d*$/.test(newValue)) {
            return;
          }
          setEditedQuantities((prev) => ({
            ...prev,
            [key]: newValue,
          }));
          return;
        }
        // For others, keep original logic
        if (newValue === "0" || (newValue.length === 1 && newValue === "0")) {
          showSnackbar("Minimum quantity should be 1", "error");
          return;
        }
        if (/^[1-9]\d*\.?\d*$/.test(newValue) || newValue === "") {
          const availableQty = existingItem.availableQuantity || 0;
          const enteredQty = parseFloat(newValue) || 0;
          if (enteredQty > availableQty) {
            showSnackbar(
              `Quantity cannot exceed available stock (${availableQty})`,
              "error"
            );
            return;
          }
          setEditedQuantities((prev) => ({
            ...prev,
            [key]: newValue,
          }));
        }
        return;
      }
    }
    // Check if this is a new item
    if (key.startsWith("new-")) {
      const partId = parseInt(key.replace("new-", ""));
      const newItem = newItems.find((item) => item.productId === partId);
      if (newItem) {
        // If partProductType is 'SERVICE', allow 0 and skip availableQuantity check
        if (newItem.partProductType === "SERVICE") {
          if (!/^\d*\.?\d*$/.test(newValue)) {
            return;
          }
          setEditedQuantities((prev) => ({
            ...prev,
            [key]: newValue,
          }));
          return;
        }
        // For others, keep original logic
        if (newValue === "0" || (newValue.length === 1 && newValue === "0")) {
          showSnackbar("Minimum quantity should be 1", "error");
          return;
        }
        if (/^[1-9]\d*\.?\d*$/.test(newValue) || newValue === "") {
          const availableQty = newItem.availableQuantity || 0;
          const enteredQty = parseFloat(newValue) || 0;
          if (enteredQty > availableQty) {
            showSnackbar(
              `Quantity cannot exceed available stock (${availableQty})`,
              "error"
            );
            return;
          }
          setEditedQuantities((prev) => ({
            ...prev,
            [key]: newValue,
          }));
        }
        return;
      }
    }
  };

  const validateQuantities = (): boolean => {
    // Check existing items
    for (const line of orderDetails?.orderLineDtos || []) {
      if (!deletedExistingItems.has(line.partId)) {
        const quantityStr = editedQuantities[`existing-${line.partId}`] || "";
        // If partProductType is 'SERVICE', allow 0 and skip availableQuantity check
        if (line.partProductType === "SERVICE") {
          if (quantityStr === "" || isNaN(Number(quantityStr))) {
            showSnackbar(`Quantity is required for ${line.partName}`, "error");
            return false;
          }
          continue;
        }
        if (quantityStr === "" || parseFloat(quantityStr) <= 0) {
          showSnackbar(`Quantity is required for ${line.partName}`, "error");
          return false;
        }
        // Validate against available quantity
        const quantity = parseFloat(quantityStr) || 0;
        const availableQty = line.availableQuantity || 0;
        if (quantity > availableQty) {
          showSnackbar(
            `Quantity for ${line.partName} cannot exceed available stock (${availableQty})`,
            "error"
          );
          return false;
        }
      }
    }
    // Check new items
    for (const item of newItems) {
      if (item.productId > 0 && !item.isOutOfStock) {
        const quantityStr = editedQuantities[`new-${item.productId}`] || "";
        // If partProductType is 'SERVICE', allow 0 and skip availableQuantity check
        if (item.partProductType === "SERVICE") {
          if (quantityStr === "" || isNaN(Number(quantityStr))) {
            showSnackbar(
              `Quantity is required for ${item.productName}`,
              "error"
            );
            return false;
          }
          continue;
        }
        if (quantityStr === "" || parseFloat(quantityStr) <= 0) {
          showSnackbar(`Quantity is required for ${item.productName}`, "error");
          return false;
        }
        // Validate against available quantity
        const quantity = parseFloat(quantityStr) || 0;
        const availableQty = item.availableQuantity || 0;
        if (quantity > availableQty) {
          showSnackbar(
            `Quantity for ${item.productName} cannot exceed available stock (${availableQty})`,
            "error"
          );
          return false;
        }
      }
    }
    return true;
  };

  const handleAddItem = () => {
    const newItem: SalesOrderItem = {
      id: 0,
      productId: 0,
      productName: "",
      quantity: 0,
      price: 0,
      tax: 0,
      externalId: 0,
      referenceCode: "",
      type: "",
      description: "",
      status: 0,
      costPrice: 0,
      averageCostPrice: 0,
      salesPrice: 0,
      penaltyType: "",
      deleted: false,
      updatedBy: 0,
      isNewItem: true,
    };

    setNewItems([...newItems, newItem]);
    // Don't set initial quantity - keep it empty
    setEditedQuantities((prev) => ({
      ...prev,
      [`new-${newItem.productId}`]: "",
    }));
  };

  const handleDeleteExistingItem = (partId: number) => {
    // Add to deleted items set
    setDeletedExistingItems((prev) => {
      const newSet = new Set(prev);
      newSet.add(partId);
      return newSet;
    });

    // Remove from selected parts
    setSelectedPartIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(partId);
      return newSet;
    });

    // Force refresh of all dropdowns
    setPartsRefreshKey((prev) => prev + 1);
  };

  const handleDeleteNewItem = (index: number) => {
    const itemToDelete = newItems[index];

    // Remove the part ID from selected parts
    if (itemToDelete.productId > 0) {
      setSelectedPartIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(itemToDelete.productId);
        return newSet;
      });

      // Remove from edited quantities
      const newQuantities = { ...editedQuantities };
      delete newQuantities[`new-${itemToDelete.productId}`];
      setEditedQuantities(newQuantities);

      // Force refresh of all dropdowns
      setPartsRefreshKey((prev) => prev + 1);
    }

    const updatedItems = newItems.filter((_, i) => i !== index);
    setNewItems(updatedItems);
  };

  const handleProductSelect =
    (index: number) =>
      (event: any, value: OptionType<SalesOrderItem> | null) => {
        const updatedNewItems = [...newItems];
        const oldPartId = updatedNewItems[index].productId;

        if (value && value.data) {
          // Remove old part ID from selected parts
          if (oldPartId > 0) {
            setSelectedPartIds((prev) => {
              const newSet = new Set(prev);
              newSet.delete(oldPartId);
              return newSet;
            });

            // Remove old quantity
            const newQuantities = { ...editedQuantities };
            delete newQuantities[`new-${oldPartId}`];
            setEditedQuantities(newQuantities);
          }

          // Add new part ID to selected parts
          setSelectedPartIds((prev) => {
            const newSet = new Set(prev);
            newSet.add(value.data.productId);
            return newSet;
          });

          // Update item with new part data
          updatedNewItems[index] = {
            ...value.data,
            quantity: 0,
            isNewItem: true,
          };
          setNewItems(updatedNewItems);

          if (oldPartId > 0) {
          }

          // Set empty quantity for new item
          setEditedQuantities((prev) => ({
            ...prev,
            [`new-${value.data.productId}`]: "",
          }));

          // Force refresh of all dropdowns
          setPartsRefreshKey((prev) => prev + 1);
        } else {
          // Remove old part ID when deselecting
          if (oldPartId > 0) {
            setSelectedPartIds((prev) => {
              const newSet = new Set(prev);
              newSet.delete(oldPartId);
              return newSet;
            });

            // Remove quantity
            const newQuantities = { ...editedQuantities };
            delete newQuantities[`new-${oldPartId}`];
            setEditedQuantities(newQuantities);

            // Force refresh of all dropdowns
            setPartsRefreshKey((prev) => prev + 1);
          }

          // Reset the item
          updatedNewItems[index] = {
            id: 0,
            productId: 0,
            productName: "",
            quantity: 0,
            price: 0,
            tax: 0,
            externalId: 0,
            referenceCode: "",
            type: "",
            description: "",
            status: 0,
            costPrice: 0,
            averageCostPrice: 0,
            salesPrice: 0,
            penaltyType: "",
            deleted: false,
            updatedBy: 0,
            isNewItem: true,
          };
          setNewItems(updatedNewItems);
        }
      };

  const handleCloseOrder = async () => {
    if (!orderDetails || !id) return;

    // Validate all quantities before closing
    if (!validateQuantities()) {
      return;
    }

    setIsClosing(true);
    try {
      // Prepare order lines with edited quantities
      const orderLines: any[] = [];

      // Add existing items with edited quantities (excluding deleted ones)
      orderDetails.orderLineDtos?.forEach((line) => {
        if (!deletedExistingItems.has(line.partId)) {
          const quantityStr = editedQuantities[`existing-${line.partId}`];
          const quantity = quantityStr
            ? parseFloat(quantityStr) || 0
            : line.quantity;

          if (quantity > 0) {
            const orderLine: UpdateSalesOrderRequestDto["orderLineDtos"][number] =
            {
              salesOrderId: orderDetails.id,
              partId: line.partId,
              partName: line.partName,
              quantity: quantity,
              price: line.price,
              warning: line.warning || false,
            };
            orderLines.push(orderLine);
          }
        }
      });

      // Add new items
      newItems.forEach((item) => {
        if (item.productId > 0 && !item.isOutOfStock) {
          const quantityStr = editedQuantities[`new-${item.productId}`];
          const quantity = quantityStr
            ? parseFloat(quantityStr) || 0
            : item.quantity;

          if (quantity > 0) {
            const orderLine: UpdateSalesOrderRequestDto["orderLineDtos"][number] =
            {
              salesOrderId: orderDetails.id,
              partId: item.productId,
              partName: item.productName,
              quantity: quantity,
              price: item.price,
              warning: item.availableQuantity
                ? item.availableQuantity < quantity
                : false,
            };
            orderLines.push(orderLine);
          }
        }
      });

      const updateDto: UpdateSalesOrderRequestDto = {
        id: orderDetails.id,
        reference: orderDetails.reference,
        creationDate: orderDetails.creationDate,
        fromWarehouseDTO: orderDetails.fromWarehouseDTO || {
          deliveryWarehouseId: 0,
          deliveryWarehouseName: "",
          deliveryWarehouseCode: "",
        },
        toWarehouseDTO: orderDetails.toWarehouseDTO || {
          deliveryWarehouseId: 0,
          deliveryWarehouseName: "",
          deliveryWarehouseCode: "",
        },
        status: "CLOSED",
        warning: orderDetails.warning || false,
        orderLineDtos: orderLines,
      };

      await SalesOrderApi.api.closeSalesOrder(updateDto);

      // Extract success message from API response
      const successMessage = "Sales order closed successfully";

      showSnackbar(successMessage, "success");

      // Navigate after a short delay to allow user to see the message
      setTimeout(() => {
        navigate(`/sales-order${statusFilter ? `?status=${statusFilter}` : ''}`);
      }, 1500);
    } catch (error) {
      console.error("Error closing sales order:", error);
      const errorMessage = extractErrorMessage(error);
      showDialog("Closing Failed", errorMessage, "error");
    } finally {
      setIsClosing(false);
      setConfirmDialogOpen(false);
      setActionType(null);
    }
  };

  const handleActionClick = (type: "close") => {
    setActionType(type);
    setConfirmDialogOpen(true);
  };

  const handleConfirmAction = () => {
    if (actionType === "close") {
      // Don't close the modal yet; show loading state
      handleCloseOrder();
    }
    // Don't close the modal here
    // setConfirmDialogOpen(false);
    // setActionType(null);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setActionType(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    if (dialogSeverity === "error") {
      // Only navigate on error if needed
      // navigate("/sales-order");
    }
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const getStatusClass = (status: string): string => {
    const statusClasses: Record<string, string> = {
      PENDING: "bg-yellow-400",
      CLOSED: "bg-gray-400",
      COMPLETED: "bg-green-400",
      ACTIVE: "bg-blue-400",
    };
    return statusClasses[status] || "bg-gray-400";
  };

  const handleBack = () => {
    navigate(`/sales-order${statusFilter ? `?status=${statusFilter}` : ''}`);
  };

  if (loadingError) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <ErrorIcon sx={{ fontSize: 48, color: "error.main", mb: 2 }} />
          <h2 className="text-xl font-semibold mb-2">Error loading order</h2>
          <p className="text-gray-600 mb-4">
            Unable to fetch order details. Please try again.
          </p>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
            Back to Sales Orders
          </button>
        </div>
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const isOrderClosed = orderDetails.status === "CLOSED";
  const isOrderPending = orderDetails.status === "PENDING";

  return (
    <Box sx={{ padding: 4 }}>
      {/* Heading */}
      <Typography
        variant="h5"
        gutterBottom
        sx={{ textAlign: "left", fontWeight: "500", color: "#1E1E1E", mb: 2 }}>
        View Sales Order
      </Typography>

      {/* Breadcrumbs */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 4,
          backgroundColor: "#F8F9FA",
          padding: "12px 16px",
          borderRadius: "8px",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
        }}>
        <div className="flex items-center bg-gray-100 px-4 py-2 rounded-md space-x-2">
          <Link
            to={`/sales-order${statusFilter ? `?status=${statusFilter}` : ''}`}
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
            Dashboard
          </Link>
          <span className="text-sm text-gray-400">/</span>
          <Link
            to={`/sales-order${statusFilter ? `?status=${statusFilter}` : ''}`}
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
            Sales
          </Link>
          <span className="text-sm text-gray-400">/</span>
          <span className="text-sm font-medium text-gray-700">View Order</span>
        </div>
      </Box>

      {/* Status and Order Info */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: "600" }}>
            Sales Order #{orderDetails?.id || "N/A"}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Created: {orderDetails?.creationDate || "N/A"}
          </Typography>
        </Box>
        <div
          className={`text-white font-semibold rounded-xl px-2 ${getStatusClass(
            orderDetails.status
          )}`}>
          {orderDetails.status || "N/A"}
        </div>
      </Box>

      {/* Form */}
      <Paper
        sx={{
          padding: 3,
          marginTop: 2,
          borderRadius: "8px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        }}>
        <Grid container spacing={3} sx={{ textAlign: "left" }}>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Reference
            </Typography>
            <TextField
              fullWidth
              variant="standard"
              disabled
              value={orderDetails.reference}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Status
            </Typography>
            <TextField
              fullWidth
              value={orderDetails.status}
              variant="standard"
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Source Warehouse
            </Typography>
            <TextField
              fullWidth
              value={
                orderDetails.fromWarehouseDTO?.deliveryWarehouseName || "N/A"
              }
              variant="standard"
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Destination Warehouse
            </Typography>
            <TextField
              fullWidth
              value={
                orderDetails.toWarehouseDTO?.deliveryWarehouseName || "N/A"
              }
              variant="standard"
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Warning Status
            </Typography>
            <TextField
              fullWidth
              value={orderDetails.warning ? "Has Warnings" : "No Warnings"}
              variant="standard"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Total Items
            </Typography>
            <TextField
              fullWidth
              value={
                (orderDetails.orderLineDtos?.filter(
                  (line) => !deletedExistingItems.has(line.partId)
                ).length || 0) + newItems.length
              }
              variant="standard"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Sales Value
            </Typography>
            <TextField
              fullWidth
              value={orderDetails.salesValue ? `₹ ${orderDetails.salesValue.toFixed(2)}` : "N/A"}
              variant="standard"
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Job ID
            </Typography>
            <TextField
              fullWidth
              value={orderDetails.jobId || "N/A"}
              variant="standard"
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Job Type
            </Typography>
            <TextField
              fullWidth
              value={orderDetails.jobType || "N/A"}
              variant="standard"
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Fleet Name
            </Typography>
            <TextField
              fullWidth
              value={orderDetails.fleetName || "N/A"}
              variant="standard"
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Part Condition
            </Typography>
            <TextField
              fullWidth
              value={
                orderDetails.partCondition
                  ? orderDetails.partCondition.replace("_", " ")
                  : "N/A"
              }
              variant="standard"
              disabled
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Items Table */}
      <Box sx={{ marginTop: 4 }}>
        <Typography
          variant="h6"
          sx={{
            textAlign: "left",
            fontWeight: "600",
            color: "#1E1E1E",
            mb: 2,
          }}>
          Items
        </Typography>
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "8px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#F8F9FA" }}>
                <TableCell sx={{ fontWeight: "600" }}>#</TableCell>
                <TableCell sx={{ fontWeight: "600" }}>Part ID</TableCell>
                <TableCell sx={{ fontWeight: "600", width: "300px" }}>
                  Product Description
                </TableCell>
                <TableCell sx={{ fontWeight: "600", width: "300px" }}>
                  Reference Code{" "}
                </TableCell>
                <TableCell sx={{ fontWeight: "600" }}>Vendors & Quantity</TableCell>
                <TableCell sx={{ fontWeight: "600" }}>Quantity</TableCell>
                <TableCell sx={{ fontWeight: "600" }}>Unit Price</TableCell>
                <TableCell sx={{ fontWeight: "600" }}>Total</TableCell>
                <TableCell sx={{ fontWeight: "600" }}>Status</TableCell>
                {isEditMode && (
                  <TableCell sx={{ fontWeight: "600" }}>Actions</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Existing Items */}
              {orderDetails.orderLineDtos &&
                orderDetails.orderLineDtos.length > 0
                ? orderDetails.orderLineDtos?.map((line, index) => {
                  if (deletedExistingItems.has(line.partId)) {
                    return null; // Don't render deleted items
                  }

                  const quantityKey = `existing-${line.partId}`;
                  const currentQuantity =
                    editedQuantities[quantityKey] !== undefined
                      ? editedQuantities[quantityKey]
                      : line.quantity.toString();
                  const numericQuantity = parseFloat(currentQuantity) || 0;
                  const availableQty = line.availableQuantity || 0;

                  return (
                    <TableRow key={`existing-${index}`}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>#{line.partId}</TableCell>
                      <TableCell>{line.partName}</TableCell>
                      <TableCell>{line.referenceCode}</TableCell>
                      <TableCell>{renderVendorAllocations(line)}</TableCell>
                      <TableCell>
                        {/* Always disabled for existing items */}
                        <TextField
                          value={currentQuantity}
                          size="small"
                          placeholder="Enter qty"
                          sx={{
                            width: "100px",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              fontSize: "14px",
                              fontWeight: 500,
                            },
                          }}
                          inputProps={{
                            max: availableQty,
                          }}
                          disabled
                        />
                      </TableCell>
                      <TableCell>
                        ₹ {line.price?.toFixed(2) || "0.00"}
                      </TableCell>
                      <TableCell>
                        ₹{" "}
                        {(numericQuantity * line.price)?.toFixed(2) || "0.00"}
                      </TableCell>
                      <TableCell>
                        {line.warning ? (
                          <span className="text-amber-600 font-medium">
                            Low Stock
                          </span>
                        ) : (
                          <span className="text-green-600 font-medium">
                            Available
                          </span>
                        )}
                      </TableCell>
                      {isEditMode && (
                        <TableCell>
                          <IconButton
                            onClick={() =>
                              handleDeleteExistingItem(line.partId)
                            }
                            sx={{
                              color: "#FF0000",
                            }}
                            size="small">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
                : !isEditMode &&
                newItems.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      sx={{ textAlign: "center", py: 4 }}>
                      No products found
                    </TableCell>
                  </TableRow>
                )}

              {/* New Items */}
              {isEditMode &&
                newItems.map((item, index) => {
                  const isOutOfStock = item.isOutOfStock || false;
                  const availableQty = item.availableQuantity || 0;
                  const quantityKey = `new-${item.productId}`;
                  const currentQuantity = editedQuantities[quantityKey] || "";
                  const numericQuantity = parseFloat(currentQuantity) || 0;
                  const visibleExistingItems =
                    orderDetails.orderLineDtos?.filter(
                      (line) => !deletedExistingItems.has(line.partId)
                    ).length || 0;
                  const rowIndex = visibleExistingItems + index + 1;

                  return (
                    <TableRow
                      key={`new-${index}`}
                      sx={{
                        backgroundColor: isOutOfStock ? "#f5f5f5" : "inherit",
                        opacity: isOutOfStock ? 0.6 : 1,
                      }}>
                      <TableCell>{rowIndex}</TableCell>
                      <TableCell>
                        {item.productId > 0 ? `#${item.productId}` : "-"}
                      </TableCell>
                      <TableCell>
                        <AppAutoCompleteWithSearchAPI
                          key={`part-select-${index}-warehouse-${orderDetails?.fromWarehouseDTO
                            ?.deliveryWarehouseId || "none"
                            }-refresh-${partsRefreshKey}`}
                          initialPageNumber={0}
                          value={
                            item.productId > 0
                              ? {
                                value: item.productId.toString(),
                                label: item.isOutOfStock
                                  ? `${item.productName} (Out of Stock)`
                                  : item.productName,
                                data: item,
                              }
                              : null
                          }
                          onChange={handleProductSelect(index)}
                          placeholder="Select product"
                          getData={fetchPaginatedPartsBySearchTerm}
                        />
                      </TableCell>
                      <TableCell>{item.referenceCode}</TableCell>
                      <TableCell>{renderVendorAllocations(item)}</TableCell>
                      <TableCell>
                        {item.productId > 0 ? (
                          <TextField
                            value={currentQuantity}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              handleQuantityChange(quantityKey, newValue);
                            }}
                            size="small"
                            placeholder="Enter qty"
                            disabled={!item.productName || isOutOfStock}
                            sx={{
                              width: "100px",
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                fontSize: "14px",
                                fontWeight: 500,
                              },
                            }}
                            inputProps={{
                              max: availableQty,
                            }}
                            // helperText={
                            //   availableQty > 0
                            //     ? `Available: ${availableQty}`
                            //     : "Out of stock"
                            // }
                            FormHelperTextProps={{
                              sx: {
                                color:
                                  numericQuantity > availableQty
                                    ? "error.main"
                                    : "text.secondary",
                              },
                            }}
                          />
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        {item.productId > 0
                          ? `₹ ${item.price?.toFixed(2) || "0.00"}`
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {item.productId > 0 && !isOutOfStock
                          ? `₹ ${(numericQuantity * item.price)?.toFixed(2) ||
                          "0.00"
                          }`
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {item.productId > 0 ? (
                          isOutOfStock ? (
                            <span className="text-red-600 font-medium">
                              Out of Stock
                            </span>
                          ) : numericQuantity > availableQty ? (
                            <span className="text-amber-600 font-medium">
                              Exceeds Stock
                            </span>
                          ) : (
                            <span className="text-green-600 font-medium">
                              Available ({availableQty})
                            </span>
                          )
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => handleDeleteNewItem(index)}
                          sx={{
                            color: "#FF0000",
                          }}
                          size="small">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add Item Button */}
        {isEditMode && (
          <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
            <IconButton
              onClick={handleAddItem}
              sx={{
                color: "#1976D2",
                mr: 1,
              }}
              size="small">
              <AddIcon fontSize="small" />
            </IconButton>
            <Typography
              variant="body1"
              sx={{
                cursor: "pointer",
                color: "#1976D2",
                fontSize: "0.875rem",
              }}
              onClick={handleAddItem}>
              Add Product
            </Typography>
          </Box>
        )}
      </Box>

      {/* Total Section */}
      {/* {(isEditMode || orderDetails.orderLineDtos?.length > 0) && (
        <Box sx={{ mt: 3, textAlign: "right" }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: "600",
              color: "#1E1E1E",
            }}>
            Total: ₹{" "}
            {calculateTotalCost(orderDetails.orderLineDtos || []).toFixed(2)}
          </Typography>
        </Box>
      )} */}

      {/* Action Buttons */}
      <Box
        sx={{
          marginTop: 4,
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
        }}>
        {isOrderPending && !isEditMode && (
          <>
            <Button
              variant="contained"
              color="primary"
              onClick={handleEditClick}
              sx={{
                padding: "10px 20px",
                borderRadius: "32px",
                textTransform: "none",
                fontWeight: "500",
              }}>
              Edit Order
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleActionClick("close")}
              sx={{
                padding: "10px 20px",
                borderRadius: "32px",
                textTransform: "none",
                fontWeight: "500",
              }}>
              Close Order
            </Button>
          </>
        )}
        <Button
          variant="outlined"
          startIcon={<HistoryIcon />}
          onClick={() => setHistoryDialogOpen(true)}
          sx={{ padding: "10px 20px", borderRadius: "32px", textTransform: "none", fontWeight: "500" }}>
          History
        </Button>

        {isEditMode && (
          <>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleCancelEdit}
              sx={{
                padding: "10px 20px",
                borderRadius: "32px",
                textTransform: "none",
                fontWeight: "500",
              }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleActionClick("close")}
              sx={{
                padding: "10px 20px",
                borderRadius: "32px",
                textTransform: "none",
                fontWeight: "500",
              }}>
              Close Order
            </Button>
          </>
        )}

        {!isEditMode && (
          <Button
            variant="outlined"
            color="primary"
            onClick={handleBack}
            sx={{
              padding: "10px 20px",
              borderRadius: "32px",
              textTransform: "none",
              fontWeight: "500",
            }}>
            Back to Sales Orders
          </Button>
        )}
      </Box>

      {orderDetails && (
        <SalesOrderHistory
          salesOrderId={orderDetails.id}
          variant="dialog"
          open={historyDialogOpen}
          onClose={() => setHistoryDialogOpen(false)}
        />
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={isClosing ? undefined : handleCloseConfirmDialog}
        TransitionComponent={Transition}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
          },
        }}>
        <DialogTitle
          id="confirm-dialog-title"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            pb: 1,
            fontWeight: 600,
            fontSize: 20,
          }}>
          <ErrorIcon sx={{ color: "warning.main", fontSize: 32 }} />
          Close Sales Order
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="confirm-dialog-description"
            sx={{
              color: "text.primary",
              fontSize: "16px",
              mt: 1,
              mb: 2,
              textAlign: "left",
            }}>
            {isEditMode
              ? "Are you sure you want to close this sales order? All changes will be saved and this action cannot be undone."
              : "Are you sure you want to close this sales order? This action cannot be undone."}
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{ px: 3, pb: 2, justifyContent: "flex-end", gap: 2 }}>
          <Button
            onClick={handleCloseConfirmDialog}
            color="primary"
            variant="outlined"
            sx={{ borderRadius: 2, minWidth: 100 }}
            disabled={isClosing}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            color="error"
            variant="contained"
            sx={{ borderRadius: 2, minWidth: 140, fontWeight: 600 }}
            autoFocus
            disabled={isClosing}
            startIcon={
              isClosing ? (
                <span
                  className="loader"
                  style={{
                    width: 18,
                    height: 18,
                    border: "2px solid #fff",
                    borderTop: "2px solid #f44336",
                    borderRadius: "50%",
                    display: "inline-block",
                    animation: "spin 1s linear infinite",
                  }}
                />
              ) : null
            }>
            {isClosing ? "Closing..." : "Yes, Close Order"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Result Dialog with Icons */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        TransitionComponent={Transition}
        aria-labelledby="result-dialog-title"
        aria-describedby="result-dialog-description"
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
          },
        }}>
        <DialogTitle
          id="result-dialog-title"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            pb: 1,
            fontWeight: 600,
            fontSize: 20,
          }}>
          {dialogSeverity === "success" ? (
            <CheckCircleIcon sx={{ color: "success.main", fontSize: 28 }} />
          ) : (
            <ErrorIcon sx={{ color: "error.main", fontSize: 28 }} />
          )}
          {dialogTitle}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="result-dialog-description"
            sx={{
              color:
                dialogSeverity === "success" ? "text.primary" : "error.main",
              fontSize: "16px",
              mt: 1,
              mb: 2,
            }}>
            {dialogMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseDialog}
            color="primary"
            variant="contained"
            sx={{ minWidth: 100, borderRadius: 2 }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for quick notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        ContentProps={{
          sx: {
            borderRadius: 2,
            p: 1,
            minWidth: 320,
            boxShadow: 3,
          },
        }}>
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: "100%",
            boxShadow: 3,
            fontSize: 16,
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            "& .MuiAlert-icon": {
              fontSize: 26,
            },
          }}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleCloseSnackbar}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );

};

export default ViewSalesOrder;
